import React, { useState } from "react";
import { Lock, FileBarChart2 } from "lucide-react";
import { useLongBallAccess } from "../../hooks/use-longball-access";
import { LongballLogo } from "./components/longball-logo";
import { PlayerReportConfig } from "./components/player-report-config";
import { TeamReportConfig } from "./components/team-report-config";
import { fetchAPIBlob } from "../../config/api";
import "./longball-page.css";

const REPORTS = [
  {
    id: "player_report",
    label: "Player Report",
    description: "Bat optimization analysis for an individual MLB player.",
  },
  {
    id: "team_report",
    label: "Team Report",
    description: "Full roster impact analysis with player improvement rankings.",
  },
];

const handlePlayerExport = async ({ player, year }) => {
  const blob = await fetchAPIBlob("/reports/keenan_report", {
    method: "POST",
    body: JSON.stringify({ player_id: player.id, year }),
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `longball_labs_${player.fullName.replace(/\s+/g, "_").toLowerCase()}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
};

const handleTeamExport = async ({ team, year }) => {
  const blob = await fetchAPIBlob("/reports/team_report", {
    method: "POST",
    body: JSON.stringify({ team_id: team.id, year }),
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `longball_labs_${team.abbr.toLowerCase()}_${year}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
};

export const LongBallPage = () => {
  const { hasAccess, loading } = useLongBallAccess();
  const [selectedReport, setSelectedReport] = useState(null);

  if (loading) {
    return (
      <div className="lbp-state">
        <div className="lbp-state-text">Loading</div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="lbp-state">
        <Lock size={32} color="#cbd5e1" />
        <div className="lbp-state-text">{"You don't have access to LongBall Labs."}</div>
      </div>
    );
  }

  return (
    <div className="lbp-root">
      <div className="lbp-inner">
        <div className="lbp-header">
          <LongballLogo scale={0.6} />
          <div>
            <div className="lbp-header-title">LongBall Labs</div>
            <div className="lbp-header-subtitle">Reports</div>
          </div>
        </div>

        <div className="lbp-reports-section">
          <div className="lbp-section-label">Select a Report</div>
          {REPORTS.map((r) => {
            const active = selectedReport === r.id;
            return (
              <button
                key={r.id}
                onClick={() => setSelectedReport(active ? null : r.id)}
                className={`lbp-report-btn${active ? " lbp-report-btn--active" : ""}`}
              >
                <FileBarChart2 size={16} />
                <div>
                  <div className="lbp-report-btn-label">{r.label}</div>
                  <div className="lbp-report-btn-desc">{r.description}</div>
                </div>
              </button>
            );
          })}
        </div>

        {selectedReport === "player_report" && (
          <div className="lbp-config-panel">
            <PlayerReportConfig onExport={handlePlayerExport} />
          </div>
        )}
        {selectedReport === "team_report" && (
          <div className="lbp-config-panel">
            <TeamReportConfig onExport={handleTeamExport} />
          </div>
        )}
      </div>
    </div>
  );
};
